// Standalone Espresso recipe parsing + copy rendering. Plain browser global, no
// build step, no dependencies.
//
// Exposes:
//   window.parseRecipeEspresso(raw)                                  -> parsed object
//   window.renderRecipeEspressoHtml(parsed, tastingHook, process, country) -> { specs, method }
//   window.renderRecipeEspressoSchema(parsed, productTitle, recipeImages)       -> Recipe JSON-LD
//
(() => {
  const FALLBACKS = {
    dose: "22g",
    yield: "44g",
    time: "22 sec",
    temp: "93.5'c",
  };

  function normalizeQuotes(value) {
    // Curly single quotes (‘ ’ ʼ) -> straight apostrophe.
    return String(value).replace(/[\u2018\u2019\u02BC]/g, "'");
  }

  // First number in a string: "44g" -> 44, "49-50g" -> 49, "22 sec" -> 22,
  // "93.5'c" -> 93.5. Unlike a strip-all-non-digits pass, this keeps the range
  // form "49-50g" as 49 instead of concatenating to 4950.
  function firstNumber(value) {
    const match = String(value).match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : 0;
  }

  function parseRecipeEspresso(raw) {
    let lines;
    if (raw == null) {
      lines = [];
    } else if (Array.isArray(raw)) {
      lines = raw;
    } else if (typeof raw === "string") {
      lines = JSON.parse(raw);
    } else {
      throw new TypeError(
        "recipe_espresso must be a JSON array string or an array of strings",
      );
    }
    if (!Array.isArray(lines)) {
      throw new TypeError("recipe_espresso parsed value must be an array");
    }
    const fields = {};
    for (const line of lines) {
      if (typeof line !== "string") continue;
      const text = normalizeQuotes(line.trim());
      if (!text) continue;
      const colon = text.indexOf(":");
      if (colon === -1) continue;
      const key = text.slice(0, colon).trim().toLowerCase();
      const value = text.slice(colon + 1).trim();
      fields[key] = value;
    }
    const dose = fields.dose ?? FALLBACKS.dose;
    const yieldValue = fields.yield ?? FALLBACKS.yield;
    const time = fields.time ?? FALLBACKS.time;
    const temp = fields.temp ?? FALLBACKS.temp;
    return {
      dose,
      doseNum: firstNumber(dose),
      yield: yieldValue,
      time,
      temp,
      yieldNum: firstNumber(yieldValue),
      timeSeconds: firstNumber(time),
    };
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/\n/g, "<br>");
  }

  function buildMethodSteps(parsed) {
    const timeLow = parsed.timeSeconds - 2;
    const timeHigh = parsed.timeSeconds + 2;
    return [
      {
        title: "Preparation",
        body:
          "Grind " +
          parsed.dose +
          " of coffee to a fine consistency, like table salt. Dose directly into your portafilter basket and give it a light shake or use a distribution tool to break up clumps." +
          "\n\n" +
          "Level the grounds with a light tap, then tamp. Tamping should be flat & level, and pressure should be firm, allowing the coffee to compact to the point it doesn’t compress any further. A crooked tamp means an uneven extraction.",
      },
      {
        title: "Pull the shot",
        body:
          "Lock the portafilter in, and start your time as you start the shot. Aim for " +
          parsed.yield +
          " of espresso in " +
          parsed.timeSeconds +
          " seconds, at " +
          parsed.temp +
          ". If you can’t change your machine temperature, don’t fret - you’ll still get a great shot of coffee out.",
      },
      {
        title: "Read the shot and adjust as needed",
        body:
          "Stop the shot at " +
          parsed.yieldNum +
          " grams, and read the extraction time." +
          "\n\n" +
          "- If the time is under " +
          timeLow +
          "s, grind a little finer and try again." +
          "\n" +
          "- If the time is over " +
          timeHigh +
          "s, grind a little coarser and try again." +
          "\n" +
          "- If the time is between " +
          timeLow +
          "s and " +
          timeHigh +
          "s, you’ve nailed it.",
      },
      {
        title: "Stir and enjoy",
        body: "Stir gently to mix, and enjoy immediately.",
      },
    ];
  }

  function formatRatio(doseNum, yieldNum) {
    if (!doseNum || !yieldNum) return "";
    const ratio = Math.round((yieldNum / doseNum) * 100) / 100;
    return "1:" + (Number.isInteger(ratio) ? ratio : ratio.toFixed(2));
  }

  /**
   * @param {object} parsed - output of parseRecipeEspresso()
   * @param {string} tastingHook - e.g. "Pomegranate, Watermelon and Hibiscus"
   * @param {string} process - coffee process, e.g. "Anaerobic Natural"
   * @param {string} country - coffee origin country, e.g. "El Salvador"
   * @returns {{specs: string, method: string}} HTML recipe markup split into
   *   the spec column (intro + tools + servings) and the method column
   *   (numbered steps). Dynamic values are HTML-escaped. Shots derive from
   *   yield (1 shot = 20g, min 2).
   */
  function renderRecipeEspressoHtml(parsed, tastingHook, process, country) {
    const shots = Math.max(2, Math.round(parsed.yieldNum / 20));
    const originClause =
      process && country
        ? "from this " +
          escapeHtml(process) +
          " " +
          escapeHtml(country) +
          " coffee"
        : "in the cup";

    const quickReferenceRows = [
      ["Dose", escapeHtml(parsed.dose)],
      ["Time", parsed.timeSeconds + " sec"],
      ["Yield", escapeHtml(parsed.yield)],
      ["Temp", escapeHtml(parsed.temp)],
      ["Ratio", formatRatio(parsed.doseNum, parsed.yieldNum)],
    ]
      .map(
        (row) =>
          "<div><span>" + row[0] + "</span><span>" + row[1] + "</span></div>",
      )
      .join("");

    const methodSteps = buildMethodSteps(parsed)
      .map(
        (step, i) =>
          "<div><strong>Step " +
          (i + 1) +
          " - " +
          escapeHtml(step.title) +
          "</strong><br>" +
          escapeHtml(step.body) +
          "</div>",
      )
      .join("");

    return {
      specs:
        "<p>A short, controlled pull that draws out " +
        escapeHtml(tastingHook) +
        " " +
        originClause +
        ". Precision over speed.</p>" +
        "<p><strong>Tools:</strong> You’ll need an espresso machine, burr grinder, tamper, digital scale (0.1g precision) and a timer.</p>" +
        "<p><strong>Servings:</strong> Makes " +
        escapeHtml(parsed.yield) +
        " of espresso, about " +
        shots +
        " shot" +
        (shots === 1 ? "" : "s") +
        ".</p>" +
        '<div class="recipe-espresso__reference">' +
        "<h3>Quick reference</h3>" +
        "<hr>" +
        '<div class="recipe-espresso__specs">' +
        quickReferenceRows +
        "</div>" +
        "</div>",
      method: "<h3>Method</h3>" + methodSteps,
    };
  }

  // ---------------------------------------------------------------------
  // Recipe JSON-LD (schema.org/Recipe). Reuses buildMethodSteps so the
  // schema step text is byte-identical to the visible method copy.
  // ---------------------------------------------------------------------

  function formatIsoDuration(timeStr) {
    const value = String(timeStr).toLowerCase();
    const colon = value.match(/(\d+):(\d{2})/);
    if (colon) {
      const minutes = Number(colon[1]);
      const seconds = Number(colon[2]);
      return seconds
        ? "PT" + minutes + "M" + seconds + "S"
        : "PT" + minutes + "M";
    }
    const sec = value.match(/(\d+)\s*sec/);
    if (sec) return "PT" + sec[1] + "S";
    return "PT0S";
  }

  // Bare quantity for schema.org recipeYield ("2 shots"). The Servings line in
  // renderRecipeEspressoHtml keeps the full sentence ("Makes 44g of espresso,
  // about 2 shots.").
  function buildYieldQuantity(parsed) {
    const shots = Math.max(2, Math.round(parsed.yieldNum / 20));
    return shots + " shot" + (shots === 1 ? "" : "s");
  }
  function buildServingsText(parsed) {
    return (
      "Makes " + parsed.yield + " of espresso, about " + buildYieldQuantity(parsed) + "."
    );
  }

  /**
   * @param {object} parsed - output of parseRecipeEspresso()
   * @param {string} productTitle - e.g. "Ethiopia Rachia"
   * @param {object[]} recipeImages - recipe_generation_images metaobject
   *   entries ({brew_method, image_1_1, image_4_3, image_16_9}), one per brew
   *   method
   * @returns {object} schema.org/Recipe JSON-LD
   */
  function renderRecipeSchema(parsed, productTitle, recipeImages) {
    const category = "Espresso";
    // Method-level finished-drink image. A bag render fails Google's "image
    // of the completed dish" requirement, so omit image when no entry matches
    // rather than falling back to the product image.
    const entry = (recipeImages || []).find((e) => e.brew_method === category);
    const images = entry
      ? [entry.image_1_1, entry.image_4_3, entry.image_16_9].filter(Boolean)
      : [];
    const image = images.length ? images : null;
    const steps = buildMethodSteps(parsed).map((step) => {
      const instruction = {
        "@type": "HowToStep",
        name: step.title,
        text: step.body,
      };
      if (image) instruction.image = image;
      return instruction;
    });
    const schema = {
      "@context": "https://schema.org",
      "@type": "Recipe",
      name: "Espresso \u2014 " + productTitle,
      totalTime: formatIsoDuration(parsed.time),
      recipeYield: buildYieldQuantity(parsed),
      // ponytail: grind descriptor mirrors the Preparation step copy.
      recipeIngredient: [parsed.dose + " coffee, ground fine"],
      recipeCategory: category,
      author: { "@type": "Organization", "name": "Deluca Specialty Coffees" },
      recipeInstructions: steps,
    };
    if (image) schema.image = image;
    return schema;
  }

  window.parseRecipeEspresso = parseRecipeEspresso;
  window.renderRecipeEspressoHtml = renderRecipeEspressoHtml;
  window.renderRecipeEspressoSchema = renderRecipeSchema;
  // Exposed in case the JSON-LD step needs to reuse this without
  // re-deriving the logic a second time (mirrors recipe-v60.js).
  window.recipeEspressoRenderHelpers = { buildMethodSteps };
})();
